import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { listsService, tasksService, tagsService } from '../services/taskService';
import { v4 as uuidv4 } from 'uuid';

const AppContext = createContext();

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within AppProvider');
  }
  return context;
};

export const AppProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [lists, setLists] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [tags, setTags] = useState([]);
  const [selectedListId, setSelectedListId] = useState('all');
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('time');
  const [showCompleted, setShowCompleted] = useState(true);
  const [loading, setLoading] = useState(false);

  // Fetch data when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
    } else {
      setLists([]);
      setTasks([]);
      setTags([]);
    }
  }, [isAuthenticated]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [listsRes, tasksRes, tagsRes] = await Promise.all([
        listsService.getAll(),
        tasksService.getAll(),
        tagsService.getAll(),
      ]);
      
      setLists(listsRes.data.map(l => ({
        ...l,
        isDefault: l.is_default,
      })));
      setTasks(tasksRes.data.map(t => ({
        ...t,
        listId: t.list_id,
        dueDate: t.due_date,
        tagIds: t.tag_ids,
        createdAt: t.created_at,
      })));
      setTags(tagsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // List operations
  const addList = useCallback(async (name) => {
    try {
      const response = await listsService.create({ name });
      const newList = {
        ...response.data,
        isDefault: response.data.is_default,
      };
      setLists(prev => [...prev, newList]);
      return newList;
    } catch (error) {
      console.error('Error creating list:', error);
      throw error;
    }
  }, []);

  const updateList = useCallback(async (listId, updates) => {
    try {
      const apiUpdates = {
        ...updates,
        is_default: updates.isDefault,
      };
      delete apiUpdates.isDefault;
      
      const response = await listsService.update(listId, apiUpdates);
      setLists(prev => prev.map(list =>
        list.id === listId ? { ...response.data, isDefault: response.data.is_default } : list
      ));
    } catch (error) {
      console.error('Error updating list:', error);
      throw error;
    }
  }, []);

  const deleteList = useCallback(async (listId) => {
    try {
      await listsService.delete(listId);
      setLists(prev => prev.filter(list => list.id !== listId));
      setTasks(prev => prev.filter(task => task.listId !== listId));
      if (selectedListId === listId) {
        setSelectedListId('all');
      }
    } catch (error) {
      console.error('Error deleting list:', error);
      throw error;
    }
  }, [selectedListId]);

  const setDefaultList = useCallback(async (listId) => {
    try {
      await listsService.update(listId, { is_default: true });
      setLists(prev => prev.map(list => ({
        ...list,
        isDefault: list.id === listId,
      })));
    } catch (error) {
      console.error('Error setting default list:', error);
      throw error;
    }
  }, []);

  // Task operations
  const addTask = useCallback(async (taskData) => {
    try {
      const defaultList = lists.find(l => l.isDefault) || lists[0];
      const apiData = {
        title: taskData.title || 'Untitled Task',
        list_id: taskData.listId || defaultList?.id,
        due_date: taskData.dueDate || null,
        notes: taskData.notes || '',
        tag_ids: taskData.tagIds || [],
      };
      
      const response = await tasksService.create(apiData);
      const newTask = {
        ...response.data,
        listId: response.data.list_id,
        dueDate: response.data.due_date,
        tagIds: response.data.tag_ids,
        createdAt: response.data.created_at,
      };
      setTasks(prev => [newTask, ...prev]);
      return newTask;
    } catch (error) {
      console.error('Error creating task:', error);
      throw error;
    }
  }, [lists]);

  const updateTask = useCallback(async (taskId, updates) => {
    try {
      const apiUpdates = {};
      if (updates.title !== undefined) apiUpdates.title = updates.title;
      if (updates.listId !== undefined) apiUpdates.list_id = updates.listId;
      if (updates.dueDate !== undefined) apiUpdates.due_date = updates.dueDate;
      if (updates.completed !== undefined) apiUpdates.completed = updates.completed;
      if (updates.notes !== undefined) apiUpdates.notes = updates.notes;
      if (updates.tagIds !== undefined) apiUpdates.tag_ids = updates.tagIds;
      
      const response = await tasksService.update(taskId, apiUpdates);
      const updatedTask = {
        ...response.data,
        listId: response.data.list_id,
        dueDate: response.data.due_date,
        tagIds: response.data.tag_ids,
        createdAt: response.data.created_at,
      };
      setTasks(prev => prev.map(task =>
        task.id === taskId ? updatedTask : task
      ));
    } catch (error) {
      console.error('Error updating task:', error);
      throw error;
    }
  }, []);

  const deleteTask = useCallback(async (taskId) => {
    try {
      await tasksService.delete(taskId);
      setTasks(prev => prev.filter(task => task.id !== taskId));
      if (selectedTaskId === taskId) {
        setSelectedTaskId(null);
      }
    } catch (error) {
      console.error('Error deleting task:', error);
      throw error;
    }
  }, [selectedTaskId]);

  const toggleTaskComplete = useCallback(async (taskId) => {
    try {
      const response = await tasksService.toggle(taskId);
      const updatedTask = {
        ...response.data,
        listId: response.data.list_id,
        dueDate: response.data.due_date,
        tagIds: response.data.tag_ids,
        createdAt: response.data.created_at,
      };
      setTasks(prev => prev.map(task =>
        task.id === taskId ? updatedTask : task
      ));
    } catch (error) {
      console.error('Error toggling task:', error);
      throw error;
    }
  }, []);

  // Subtask operations
  const addSubtask = useCallback(async (taskId, title) => {
    try {
      const response = await tasksService.addSubtask(taskId, title);
      const updatedTask = {
        ...response.data,
        listId: response.data.list_id,
        dueDate: response.data.due_date,
        tagIds: response.data.tag_ids,
        createdAt: response.data.created_at,
      };
      setTasks(prev => prev.map(task =>
        task.id === taskId ? updatedTask : task
      ));
    } catch (error) {
      console.error('Error adding subtask:', error);
      throw error;
    }
  }, []);

  const toggleSubtaskComplete = useCallback(async (taskId, subtaskId) => {
    try {
      const response = await tasksService.toggleSubtask(taskId, subtaskId);
      const updatedTask = {
        ...response.data,
        listId: response.data.list_id,
        dueDate: response.data.due_date,
        tagIds: response.data.tag_ids,
        createdAt: response.data.created_at,
      };
      setTasks(prev => prev.map(task =>
        task.id === taskId ? updatedTask : task
      ));
    } catch (error) {
      console.error('Error toggling subtask:', error);
      throw error;
    }
  }, []);

  const deleteSubtask = useCallback(async (taskId, subtaskId) => {
    try {
      const response = await tasksService.deleteSubtask(taskId, subtaskId);
      const updatedTask = {
        ...response.data,
        listId: response.data.list_id,
        dueDate: response.data.due_date,
        tagIds: response.data.tag_ids,
        createdAt: response.data.created_at,
      };
      setTasks(prev => prev.map(task =>
        task.id === taskId ? updatedTask : task
      ));
    } catch (error) {
      console.error('Error deleting subtask:', error);
      throw error;
    }
  }, []);

  // Get selected task
  const selectedTask = tasks.find(t => t.id === selectedTaskId) || null;

  const value = {
    // State
    lists,
    tasks,
    tags,
    selectedListId,
    selectedTaskId,
    selectedTask,
    searchQuery,
    sortBy,
    showCompleted,
    loading,

    // Setters
    setSelectedListId,
    setSelectedTaskId,
    setSearchQuery,
    setSortBy,
    setShowCompleted,

    // List operations
    addList,
    updateList,
    deleteList,
    setDefaultList,
    fetchData,

    // Task operations
    addTask,
    updateTask,
    deleteTask,
    toggleTaskComplete,

    // Subtask operations
    addSubtask,
    deleteSubtask,
    toggleSubtaskComplete,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};
